import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { useContext } from 'react';
import UnderlineLink from '@comp/UnderlineLink/UnderlineLink';
import H4 from '@base/H4/H4';
import H2 from '@base/H2/H2';
import P1 from '@base/P1/P1';
import { Context as PurchaseTypeContext } from '@context/PurchaseTypeContext';
import { CHECKOUT_STARTED } from '@constants/segment';
import segmentStorage from '@events/segmentStorage';
import { segment } from '@actions/analytics';
import { evtNameCategoryMap } from '@constants/index';
import { getSourcesFromUrlMap } from '@utils/resourceHelper';
import { Spacings } from '@styles/index';
import usePurchaseCTAHandler from '@hooks/usePurchaseCTAHandler';
import VideoLazyLoad from '@base/VideoLazyLoad/VideoLazyLoad';
import useGoalPageStaticContent from '@api/hooks/goal/useGoalPageStaticContent';

const data = (goalName, staticData) => {
  if (staticData[0]?.title === 'default') {
    return [
      {
        ...staticData[0],
        title: `The best way to prepare for ${goalName}`
      },
      ...staticData.slice(1)
    ];
  }
  return staticData;
};

const Container = styled.section`
  position: relative;
  margin: auto;
  margin-top: 120px;
  border-radius: 8px;
  width: 1136px;
  @media (max-width: 70.95em) {
    width: 728px;
  }
  @media (max-width: 47.95em) {
    margin-top: 88px;
    width: 100%;
    padding: 0 16px;
  }
`;

const StyledH2 = styled(H2)`
  line-height: 120%;
  font-size: 40px;
  @media (max-width: 70.95em) {
    font-size: 28px;
    line-height: 120%;
  }
  @media (max-width: 47.95em) {
    text-align: center;
  }
`;

const StyledH4 = styled(H4)`
  font-size: 32px;
  margin-top: 40px;
  @media only screen and (max-width: 70.95em) {
    font-size: 24px;
  }
  @media only screen and (max-width: 47.95em) {
    margin-top: 24px;
    font-size: 22px;
  }
`;

const SubSection = styled.div`
  display: flex;
  margin-bottom: 160px;
  @media (max-width: 47.95em) {
    flex-direction: column;
    margin-bottom: 72px;
  }
`;

const Left = styled.div`
  position: sticky;
  top: 80px;
  width: 416px;
  @media (max-width: 70.95em) {
    width: 325px;
  }
  @media (max-width: 47.95em) {
    width: 100%;
    position: relative;
    top: 0;
  }
`;

const TextWrapper = styled.div`
  flex: 1;
  position: relative;
`;

const Right = styled.div`
  flex: 1;
  & > :not(:last-child) {
    margin-bottom: 160px;
  }
  @media (max-width: 70.95em) {
    width: 356px;
    & > :not(:last-child) {
      margin-bottom: 64px;
    }
  }
  @media (max-width: 47.95em) {
    width: 100%;
    margin-top: 40px;
    & > :not(:last-child) {
      margin-bottom: 0;
    }
  }
`;

const FeatureCard = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
`;

const Description = styled(P1)`
  margin-top: 24px;
  width: 416px;
  @media only screen and (max-width: 70.95em) {
    width: 100%;
  }
  @media only screen and (max-width: 47.95em) {
    width: 100%;
    margin-top: 12px;
  }
`;

const Video = styled.video`
  width: 600px;
  height: 350px;
  object-fit: cover;
  @media (max-width: 70.95em) {
    width: 356px;
    height: 182px;
  }
  @media (max-width: 47.95em) {
    width: 100%;
    height: 184px;
  }
`;

const GreenH4 = styled(H4)`
  font-weight: bold;
  font-size: 20px;
  line-height: 150%;
  color: var(--color-i-green);
  @media (max-width: 70.95em) {
    font-size: 16px;
  }
`;

const LinkWrapper = styled.div`
  margin-top: ${Spacings.SPACING_10B};
  @media (max-width: 70.95em) {
    margin-top: 24px;
  }
  @media (max-width: 47.95em) {
    display: none;
  }
`;

const Indicator = styled.div`
  outline: 0;
  padding: 0;
  border: none;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--color-divider);
  margin-right: 8px;
  @media (max-width: 47.95em) {
    &.active {
      background: var(--color-i-green);
    }
  }
`;

const Flex = styled.div`
  display: flex;
  justify-content: center;
  @media (max-width: 47.95em) {
    margin-top: 32px;
    & > div:last-child {
      margin-right: 0;
    }
  }
`;

const SubscriptionBenefitsSection = ({ goalUID, goalSlug, goalDetails }) => {
  const purchaseType = useContext(PurchaseTypeContext);
  const { name, isNoTestGoal } = goalDetails;
  let {
    goalStaticContent: { features: staticBanners }
  } = useGoalPageStaticContent(goalUID);
  const { goalStaticContent: defaultStaticContent } =
    useGoalPageStaticContent('__default') || {};
  if (!staticBanners && defaultStaticContent)
    staticBanners = defaultStaticContent.features || [];
  staticBanners = data(goalDetails.name, staticBanners);
  if (isNoTestGoal) {
    staticBanners = staticBanners.filter((feature) => feature.key !== 'tests');
  }

  const CTALabel = usePurchaseCTAHandler(goalUID);

  const handleAnalytics = () => {
    segment.track(
      CHECKOUT_STARTED,
      {
        purchase_type: purchaseType,
        goal_uid: goalUID,
        goal_name: name,
        is_subscription_active: false,
        is_upgrade: false,
        last_primary_source: segmentStorage.getItem('lps')
      },
      evtNameCategoryMap.BOTH
    );
    segmentStorage.setItem('lps', 'goal');
  };
  const renderItems = (dataItem) => {
    return dataItem.highlights.map((featureData, featureIndex) => {
      return (
        <FeatureCard key={String(featureIndex)}>
          <Video loop muted autoPlay playsInline className="lazy-video">
            {getSourcesFromUrlMap(featureData.urls, false, true)}
          </Video>
          <StyledH4>{featureData.title}</StyledH4>
          <Description>{featureData.subTitle}</Description>
        </FeatureCard>
      );
    });
  };
  return (
    <Container>
      {staticBanners.map((dataItem, index) => {
        return (
          <SubSection key={String(index)}>
            <TextWrapper>
              <Left>
                <StyledH2>{dataItem.title}</StyledH2>
                <LinkWrapper>
                  <UnderlineLink
                    href="/goal/[goalSlug]/[goalUID]/subscribe"
                    as={`/goal/${goalSlug}/${goalUID}/subscribe`}
                    color="var(--color-i-green)"
                    onClick={handleAnalytics}
                  >
                    <GreenH4 data-analytics="goal-getSubscription">
                      {CTALabel}
                    </GreenH4>
                  </UnderlineLink>
                </LinkWrapper>
              </Left>
            </TextWrapper>
            <Right>
              <VideoLazyLoad>{renderItems(dataItem)}</VideoLazyLoad>
            </Right>
          </SubSection>
        );
      })}
    </Container>
  );
};

const Indicators = ({ count, active }) => {
  return (
    <Flex>
      {[...Array(count).keys()].map((_count, index) => (
        <Indicator
          key={String(_count)}
          className={index === active ? 'active' : ''}
        />
      ))}
    </Flex>
  );
};

Indicators.propTypes = {
  count: PropTypes.number.isRequired,
  active: PropTypes.number.isRequired
};

SubscriptionBenefitsSection.propTypes = {
  goalUID: PropTypes.string.isRequired,
  goalSlug: PropTypes.string.isRequired,
  goalDetails: PropTypes.objectOf(PropTypes.any).isRequired
};

export default SubscriptionBenefitsSection;
